import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usersAPI, listingsAPI, reviewsAPI, reportsAPI, messagesAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useUserCounts } from '@/contexts/UserCountsContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ReportImageUpload } from '@/components/reports/ReportImageUpload';
import ListingCard from '@/components/listings/ListingCard';
import ReviewCard from '@/components/reviews/ReviewCard';
import { formatDate, formatRelativeTime, getInitials, getFullImageUrl } from '@/lib/utils';
import { toast } from 'sonner';
import { useListingGrid } from '@/hooks/useListingGrid';
import { 
    Star, 
    MapPin, 
    Calendar, 
    ShieldCheck, 
    Package,
    Clock,
    MessageSquare,
    AlertTriangle,
    Flag,
    Loader2,
    Send,
    UserPlus,
    UserMinus,
    Users
} from 'lucide-react';

const UserProfilePage = () => {
    const { username } = useParams();
    const navigate = useNavigate();
    const { user: currentUser, isAuthenticated } = useAuth();
    const { incrementCount, decrementCount } = useUserCounts();
    const [user, setUser] = useState(null);
    const [listings, setListings] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Scroll to top when page loads or username changes
    useEffect(() => {
        // Use instant scroll and ensure it runs after render
        requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: 'instant' });
        });
    }, [username]);
    
    // Grid settings using shared hook
    const { gridSettings, singleColumn } = useListingGrid();
    
    // Active section state
    const [activeSection, setActiveSection] = useState('sales');
    
    // Report state
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [submittingReport, setSubmittingReport] = useState(false);
    const [reportImages, setReportImages] = useState([]);
    const [uploadingReportImages, setUploadingReportImages] = useState(false);
    
    // Message state
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    
    // Follow state
    const [isFollowing, setIsFollowing] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);
    const [followLoading, setFollowLoading] = useState(false);

    // Handle sending message to user
    const handleSendMessage = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a trimite mesaje');
            navigate('/login');
            return;
        }
        
        if (!messageText.trim()) {
            toast.error('Scrie un mesaj');
            return;
        }
        
        setSendingMessage(true);
        try {
            const res = await messagesAPI.send({
                receiver_id: user.user_id,
                content: messageText.trim()
            });
            toast.success('Mesaj trimis!');
            setMessageDialogOpen(false);
            setMessageText('');
            // Navigate to the conversation
            if (res.data?.conversation_id) {
                navigate(`/messages/${res.data.conversation_id}`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea mesajului');
        } finally {
            setSendingMessage(false);
        }
    };
    
    // Handle follow/unfollow
    const handleToggleFollow = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a urmări vânzători');
            navigate('/login');
            return;
        }
        
        setFollowLoading(true);
        try {
            if (isFollowing) {
                await usersAPI.unfollowSeller(user.user_id);
                setIsFollowing(false);
                setFollowersCount(prev => Math.max(0, prev - 1));
                decrementCount('following');
                toast.success(`Nu mai urmărești pe ${user.name || user.username}`);
            } else {
                await usersAPI.followSeller(user.user_id);
                setIsFollowing(true);
                setFollowersCount(prev => prev + 1);
                incrementCount('following');
                toast.success(`Urmărești acum pe ${user.name || user.username}. Vei primi notificări când adaugă piese noi.`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la actualizarea urmăririi');
        } finally {
            setFollowLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch user profile
                const userRes = await usersAPI.getByUsername(username);
                setUser(userRes.data);
                
                // Fetch followers count
                try {
                    const followersRes = await usersAPI.getFollowersCount(userRes.data.user_id);
                    setFollowersCount(followersRes.data?.followers_count || 0);
                } catch (e) {
                    setFollowersCount(0);
                }
                
                // Check if current user follows this user
                if (isAuthenticated && currentUser?.user_id !== userRes.data.user_id) {
                    try {
                        const followRes = await usersAPI.checkFollowing(userRes.data.user_id);
                        setIsFollowing(followRes.data?.following || false);
                    } catch (e) {
                        setIsFollowing(false);
                    }
                }
                
                // Fetch user's listings
                const listingsRes = await listingsAPI.getAll({ 
                    seller_username: username,
                    status: 'active',
                    limit: 20 
                });
                setListings(listingsRes.data?.listings || listingsRes.data || []);
                
                // Fetch user's reviews
                try {
                    const reviewsRes = await reviewsAPI.getByUsername(username);
                    setReviews(reviewsRes.data || []);
                } catch (e) {
                    setReviews([]);
                }
            } catch (err) {
                console.error('Error fetching profile:', err);
                setError('Utilizatorul nu a fost găsit');
            } finally {
                setLoading(false);
            }
        };

        if (username) {
            fetchData();
        }
    }, [username]);

    // Handle report submission
    const handleReport = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a raporta');
            return;
        }
        
        if (!reportReason) {
            toast.error('Selectează un motiv pentru raport');
            return;
        }
        
        setSubmittingReport(true);
        try {
            await reportsAPI.createWithImages({
                report_type: 'user',
                target_id: user.user_id,
                target_title: user.username,
                reason: reportReason,
                description: reportDescription.trim(),
            }, reportImages);
            toast.success('Raportul a fost trimis. Mulțumim!');
            setReportDialogOpen(false);
            setReportReason('');
            setReportDescription('');
            // Clean up image previews
            reportImages.forEach(img => URL.revokeObjectURL(img.preview));
            setReportImages([]);
        } catch (error) {
            toast.error('Eroare la trimiterea raportului');
        } finally {
            setSubmittingReport(false);
        }
    };

    // Check if this is the current user's own profile
    const isOwnProfile = currentUser?.username === username;

    if (loading) {
        return (
            <div className="min-h-screen bg-background py-8">
                <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
                    <div className="flex gap-6 mb-8">
                        <Skeleton className="w-24 h-24 rounded-full" />
                        <div className="flex-1 space-y-3">
                            <Skeleton className="h-8 w-48" />
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-4 w-64" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1,2,3,4,5,6].map(i => (
                            <Skeleton key={i} className="h-[350px] rounded-3xl" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error || !user) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h2 className="font-heading text-2xl font-bold mb-2">Utilizator negăsit</h2>
                    <p className="text-muted-foreground mb-4">{error || 'Acest profil nu există'}</p>
                    <Link to="/" className="text-primary hover:underline">
                        Înapoi acasă
                    </Link>
                </div>
            </div>
        );
    }

    // Generate SEO data
    const profileUrl = `https://colectarium.ro/user/${user.username}`;
    const profileDescription = user.bio 
        ? user.bio.substring(0, 160) 
        : `Profil ${user.name || user.username} pe Colectarium. ${listings.length} listări active, ${user.rating_count || 0} recenzii. Vânzător de piese de colecție - numismatică, faleristică, militare.`;
    
    // JSON-LD for Person/Seller
    const personSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": user.name || user.username,
        "url": profileUrl,
        "image": user.picture || undefined,
        "description": profileDescription,
        "memberOf": {
            "@type": "Organization",
            "name": "Colectarium",
            "url": "https://colectarium.ro"
        },
        ...(user.rating && {
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": user.rating.toFixed(1),
                "reviewCount": user.rating_count || 0,
                "bestRating": 5,
                "worstRating": 1
            }
        })
    };
    
    // Breadcrumb schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Acasă",
                "item": "https://colectarium.ro/"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "Vânzători",
                "item": "https://colectarium.ro/sellers"
            },
            {
                "@type": "ListItem",
                "position": 3,
                "name": user.name || user.username,
                "item": profileUrl
            }
        ]
    };

    return (
        <div className="min-h-screen bg-background" data-testid="user-profile-page">
            {/* SEO Meta Tags */}
            <Helmet>
                <title>{user.name || user.username} - Vânzător pe Colectarium | Piese de Colecție</title>
                <meta name="description" content={profileDescription} />
                <meta name="keywords" content={`${user.username}, vânzător, colectarium, piese colecție, numismatică, faleristică, militare`} />
                <link rel="canonical" href={profileUrl} />
                
                {/* Open Graph */}
                <meta property="og:type" content="profile" />
                <meta property="og:url" content={profileUrl} />
                <meta property="og:title" content={`${user.name || user.username} - Vânzător pe Colectarium`} />
                <meta property="og:description" content={profileDescription} />
                <meta property="og:image" content={user.picture || 'https://www.colectarium.ro/og-image.jpg'} />
                <meta property="og:site_name" content="Colectarium" />
                <meta property="og:locale" content="ro_RO" />
                <meta property="profile:username" content={user.username} />
                
                {/* Twitter */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={`${user.name || user.username} - Vânzător pe Colectarium`} />
                <meta name="twitter:description" content={profileDescription} />
                <meta name="twitter:image" content={user.picture || 'https://www.colectarium.ro/og-image.jpg'} />
                
                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(personSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            </Helmet>

            {/* Profile Header */}
            <div className="bg-white border-b">
                <div className="container mx-auto px-4 lg:px-8 py-8 max-w-5xl">
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                        {/* Avatar */}
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-primary flex items-center justify-center">
                            {user.picture ? (
                                <img 
                                    src={getFullImageUrl(user.picture)}
                                    alt={user.name || user.username}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <span 
                                className="text-white text-2xl font-medium items-center justify-center w-full h-full"
                                style={{ display: user.picture ? 'none' : 'flex' }}
                            >
                                {getInitials(user.name || user.username)}
                            </span>
                        </div>

                        {/* Info */}
                        <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                                <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                                    {user.name || user.username}
                                </h1>
                                {user.is_verified && (
                                    <Badge className="bg-green-50 text-green-700">
                                        <ShieldCheck className="w-3 h-3 mr-1" />
                                        Verificat
                                    </Badge>
                                )}
                                {user.is_seller_verified && (
                                    <Badge className="bg-blue-50 text-blue-700">
                                        Vânzător verificat
                                    </Badge>
                                )}
                            </div>
                            
                            {/* Rating displayed prominently under name */}
                            <div className="flex items-center gap-2 mt-2">
                                <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star 
                                            key={star} 
                                            className={`w-5 h-5 ${star <= Math.round(user.rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                                        />
                                    ))}
                                </div>
                                <span className="font-semibold text-lg">{(user.rating || 0).toFixed(1)}</span>
                                <span className="text-muted-foreground">
                                    ({user.rating_count || 0} recenzii)
                                </span>
                            </div>
                            
                            {/* Warnings display - visible if any */}
                            {(user.warnings_count || 0) > 0 && (
                                <div className={`flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg ${
                                    user.warnings_count >= 3 ? 'bg-red-50 text-red-700' : 'bg-orange-50 text-orange-700'
                                }`}>
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="font-medium">
                                        {user.warnings_count} avertisment{user.warnings_count !== 1 ? 'e' : ''} de la administrație
                                    </span>
                                </div>
                            )}

                            {/* Stats */}
                            <div className="flex flex-wrap gap-4 mt-4">
                                {followersCount > 0 && (
                                    <div className="flex items-center gap-1.5 text-muted-foreground">
                                        <Users className="w-4 h-4" />
                                        <span>{followersCount} {followersCount === 1 ? 'urmăritor' : 'urmăritori'}</span>
                                    </div>
                                )}
                                {user.transactions_completed > 0 && (
                                    <div className="flex items-center gap-1.5 text-muted-foreground">
                                        <Package className="w-4 h-4" />
                                        <span>{user.transactions_completed} tranzacții</span>
                                    </div>
                                )}
                                {user.location && (
                                    <div className="flex items-center gap-1.5 text-muted-foreground">
                                        <MapPin className="w-4 h-4" />
                                        <span>{user.location}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                    <Calendar className="w-4 h-4" />
                                    <span>Membru din {formatDate(user.created_at)}</span>
                                </div>
                            </div>

                            {/* Badges */}
                            {user.badges?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-4">
                                    {user.badges.map((badge) => (
                                        <Badge key={badge} variant="secondary" className="capitalize">
                                            {badge.replace('_', ' ')}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                            
                            {/* Action Buttons - Order: Vânzări, Trimite mesaj, Recenzii, Raportează */}
                            <div className="mt-4 flex flex-wrap gap-2">
                                {/* Sales Button */}
                                <Button 
                                    variant={activeSection === 'sales' ? 'default' : 'outline-solid'}
                                    size="sm"
                                    className="rounded-full"
                                    onClick={() => setActiveSection('sales')}
                                    data-testid="sales-btn"
                                >
                                    <Package className="w-4 h-4 mr-2" />
                                    Vânzări ({listings.length})
                                </Button>
                                
                                {/* Send Message Button - only for authenticated users viewing other profiles */}
                                {isAuthenticated && !isOwnProfile && (
                                    <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
                                        <DialogTrigger asChild>
                                            <Button 
                                                variant="outline"
                                                size="sm"
                                                className="rounded-full"
                                                data-testid="send-message-btn"
                                            >
                                                <Send className="w-4 h-4 mr-2" />
                                                Trimite mesaj
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Trimite mesaj</DialogTitle>
                                                <DialogDescription>
                                                    Trimite un mesaj către @{user.username}
                                                </DialogDescription>
                                            </DialogHeader>
                                            <div className="space-y-4 py-4">
                                                <Textarea
                                                    value={messageText}
                                                    onChange={(e) => setMessageText(e.target.value)}
                                                    placeholder="Scrie mesajul tău aici..."
                                                    rows={4}
                                                    data-testid="message-textarea"
                                                />
                                            </div>
                                            <DialogFooter>
                                                <Button 
                                                    variant="outline" 
                                                    onClick={() => setMessageDialogOpen(false)}
                                                >
                                                    Anulează
                                                </Button>
                                                <Button
                                                    onClick={handleSendMessage}
                                                    disabled={sendingMessage || !messageText.trim()}
                                                    data-testid="submit-message-btn"
                                                >
                                                    {sendingMessage ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Se trimite...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Send className="w-4 h-4 mr-2" />
                                                            Trimite
                                                        </>
                                                    )}
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                )}
                                
                                {/* Follow Button - only for authenticated users viewing other profiles */}
                                {isAuthenticated && !isOwnProfile && (
                                    <Button 
                                        variant={isFollowing ? "default" : "outline"}
                                        size="sm"
                                        className={`rounded-full ${isFollowing ? 'bg-primary hover:bg-primary/90' : ''}`}
                                        onClick={handleToggleFollow}
                                        disabled={followLoading}
                                        data-testid="follow-btn"
                                    >
                                        {followLoading ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : isFollowing ? (
                                            <UserMinus className="w-4 h-4 mr-2" />
                                        ) : (
                                            <UserPlus className="w-4 h-4 mr-2" />
                                        )}
                                        {isFollowing ? 'Urmărești' : 'Urmărește'}
                                    </Button>
                                )}
                                
                                {/* Reviews Button */}
                                <Button 
                                    variant={activeSection === 'reviews' ? 'default' : 'outline-solid'}
                                    size="sm"
                                    className="rounded-full"
                                    onClick={() => setActiveSection('reviews')}
                                    data-testid="reviews-btn"
                                >
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Recenzii ({reviews.length})
                                </Button>
                                
                                {/* Report User Button - only for authenticated users viewing other profiles */}
                                {isAuthenticated && !isOwnProfile && (
                                    <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                                        <DialogTrigger asChild>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                className="rounded-full text-muted-foreground hover:text-red-600 hover:border-red-300"
                                                data-testid="report-user-btn"
                                            >
                                                <Flag className="w-4 h-4 mr-2" />
                                                Raportează
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Raportează utilizatorul</DialogTitle>
                                                <DialogDescription>
                                                    Raportezi utilizatorul @{user.username}. Descrie problema întâmpinată.
                                                </DialogDescription>
                                            </DialogHeader>
                                            <div className="space-y-4 py-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="report-reason">Motiv</Label>
                                                    <Select value={reportReason} onValueChange={setReportReason}>
                                                        <SelectTrigger id="report-reason" data-testid="report-reason-select">
                                                            <SelectValue placeholder="Selectează motivul" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="fraud">Fraudă / Înșelăciune</SelectItem>
                                                            <SelectItem value="harassment">Hărțuire</SelectItem>
                                                            <SelectItem value="fake_profile">Profil fals</SelectItem>
                                                            <SelectItem value="spam">Spam</SelectItem>
                                                            <SelectItem value="inappropriate">Comportament inadecvat</SelectItem>
                                                            <SelectItem value="other">Altele</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="report-description">Descriere (opțional)</Label>
                                                    <Textarea
                                                        id="report-description"
                                                        value={reportDescription}
                                                        onChange={(e) => setReportDescription(e.target.value)}
                                                        placeholder="Descrie problema în detaliu..."
                                                        rows={4}
                                                        data-testid="report-description"
                                                    />
                                                </div>
                                                <ReportImageUpload
                                                    images={reportImages}
                                                    setImages={setReportImages}
                                                    uploading={uploadingReportImages}
                                                    setUploading={setUploadingReportImages}
                                                />
                                            </div>
                                            <DialogFooter>
                                                <Button 
                                                    variant="outline" 
                                                    onClick={() => setReportDialogOpen(false)}
                                                >
                                                    Anulează
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    onClick={handleReport}
                                                    disabled={submittingReport || !reportReason}
                                                    data-testid="submit-report-btn"
                                                >
                                                    {submittingReport ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Se trimite...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Flag className="w-4 h-4 mr-2" />
                                                            Trimite raportul
                                                        </>
                                                    )}
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="container mx-auto px-4 lg:px-8 py-8 max-w-5xl">
                {/* Sales Section */}
                {activeSection === 'sales' && (
                    <>
                        {listings.length > 0 ? (
                            <div 
                                className="grid gap-6 category-grid"
                                style={{
                                    '--grid-cols-mobile': gridSettings.mobile,
                                    '--grid-cols-tablet': gridSettings.tablet,
                                    '--grid-cols-desktop': gridSettings.desktop,
                                }}
                            >
                                {listings.map(listing => (
                                    <ListingCard 
                                        key={listing.listing_id} 
                                        listing={listing} 
                                        singleColumn={singleColumn}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Card className="rounded-2xl">
                                <CardContent className="py-12 text-center">
                                    <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">Nicio listare activă</p>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}

                {/* Reviews Section */}
                {activeSection === 'reviews' && (
                    <>
                        {reviews.length > 0 ? (
                            <div className="space-y-4">
                                {reviews.map(review => (
                                    <ReviewCard 
                                        key={review.review_id} 
                                        review={review}
                                        currentUserId={currentUser?.user_id}
                                        onReplyUpdate={(reviewId, reply) => {
                                            setReviews(prev => prev.map(r => 
                                                r.review_id === reviewId 
                                                    ? { ...r, reply } 
                                                    : r
                                            ));
                                        }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Card className="rounded-2xl">
                                <CardContent className="py-12 text-center">
                                    <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">Nicio recenzie încă</p>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default UserProfilePage;
